import React, { useContext, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import {
    makeStyles,
    Popover,
    PopoverSurface,
    PopoverTrigger,
} from "@fluentui/react-components";
import { AuctionSeats, Suits1, SuitSymbols, SuitColors } from './BridgeTypes';
import { GameSettingsContext, SkillLevel } from './BridgeEngineSettings';
import { PlayStateContext } from './BridgeEnginePlay';
import BridgeMarkdown from './BridgeMarkdown';
import './Auction.css';

const Auction: React.FC = () => {
    const playState = useContext(PlayStateContext)!;
    const [bids, setBids] = useState<string[]>(playState.boardState.auction);
    const { auctionNotes } = playState.boardState;

    const gameSettingsContext = useContext(GameSettingsContext)!;
    const { gameSettings } = gameSettingsContext;

    useEffect(() => {
        if (playState) {
            let auction: string[] = playState.boardState.auction;
            // rotate the auction to start with the dealer by padding early bids.
            if (playState.boardState.dealerSeat !== undefined) {
                const dealerIndex = AuctionSeats.indexOf(playState.boardState.dealerSeat);
                if (dealerIndex > 0) {
                    auction = Array(dealerIndex).fill('').concat(auction);
                }
            }
            setBids(auction);
        }
    }, [playState, playState.boardState.auction]);

    const renderNoteText = (notes: string[]): JSX.Element => {
        let text = notes.join('\n');
        return (
            <BridgeMarkdown className='popover-body'>
                {text}
            </BridgeMarkdown> 
        );
    }
    
    const renderBidAndNote = (bid: string): JSX.Element => {
        if (!bid) {
            return (<div></div>); 
        }

        if (auctionNotes && auctionNotes.length > 0) {
            const matchingNotes: string[] = playState.boardState.auctionNotes
                .filter(
                    (note) => note.bid.toLowerCase() === bid.toLowerCase() &&
                        Object.values(SkillLevel).indexOf(note.level) >= Object.values(SkillLevel).indexOf(gameSettings.skillLevel)
                )
                .flatMap((note) => note.notes);

            if (matchingNotes.length > 0) {
                return (
                    <Popover withArrow>
                        <PopoverTrigger disableButtonEnhancement>
                            {renderBid(bid, true)}
                        </PopoverTrigger>
                        <PopoverSurface tabIndex={-1}>
                            <div>
                                {renderNoteText(matchingNotes)}
                            </div>
                        </PopoverSurface>
                    </Popover>
                );
            }
        }

        return (
            <>{renderBid(bid, false)}</>
        );
    }
    
    const renderBid = (bid: string, isNote: boolean): JSX.Element => {
        if (!bid) {
            return (<div></div>);
        }
        
        // make sure there's at least a second character there to avoid length tests below.
        bid = bid.padEnd(2, ' ').toLowerCase();

        // normalize this for the switch.
        let bidType = bid.trim().toUpperCase();
        // only auctionNotes cares about what follows the P.
        if (bidType.charAt(0) === 'P') {
            bidType = 'P';
        }

        let buttonContent: JSX.Element;

        switch (bidType) {
            case 'AP':
                buttonContent = <>All Pass</>;
                break;
            case 'X':
                buttonContent = <>Dbl</>;
                break;
            case 'XX':
                buttonContent = <>Rdbl</>;
                break;
            case 'P':
                buttonContent = <>Pass</>;
                break;
            default:
                if (!isNaN(Number(bid.charAt(0)))) {
                    buttonContent = (
                    <>
                        {bid.charAt(0)}&nbsp;
                        {bid.charAt(1) === 'n' ? (
                            <span className="bid-nt">NT</span>
                        ) : (
                            Suits1.includes(bid.charAt(1)) && (
                                <span className="bid-suit" style={{ color: SuitColors[Suits1.indexOf(bid.charAt(1))] }}>
                                {SuitSymbols[Suits1.indexOf(bid.charAt(1))]}
                                </span>
                            )
                        )}
                    </>
                    );
                } else {
                    buttonContent = <>{bid}</>;
                }
                break;
        }

        return (
            <button className={['AP', 'X', 'XX', 'P'].includes(bidType) ? "bid-pass" : "bid-rank"}>
                {buttonContent}
                {isNote && <sup className="bid-note-mark">*</sup>}
            </button>
        );
    }

    return (
        <div className="div-auction">
            <table id="auction-table">
                <thead>
                    <tr>
                        {AuctionSeats.map((seat) => (
                            <th key={seat}>{seat.charAt(0).toUpperCase() + seat.slice(1)}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {bids.map((bid, index) => {
                        if (index % 4 === 0) {
                            return (
                                <tr key={index}>
                                    {[0, 1, 2, 3].map((offset) => (
                                        <td key={offset}>
                                            {renderBidAndNote(bids[index + offset])}
                                        </td>
                                    ))}
                                </tr>
                            );
                        }
                        return null;
                    })}
                </tbody>
            </table>
        </div>
    );
};

export default Auction;